import React, { useCallback, useMemo, useState } from "react"
import { apiV2 } from "../../../repositories/repository"
import useSWR from "swr"
import { useHistory, useParams } from "react-router-dom"
import { Chart } from "chart.js/auto"
import './PatientGameStats.scss'
import { translate } from "../../../selectors/translations"
import { ReactComponent as BackIcon } from '../../../assets/img/icons/arrow_left_icon.svg'
import { RouteNames } from "../../../types/constants"
import { DatePicker, Tabs } from "rsuite"
import 'rsuite/DatePicker/styles/index.css'
import 'rsuite/Tabs/styles/index.css'
import 'rsuite/SelectPicker/styles/index.css'
import dayjs from "dayjs"
import annotationPlugin from 'chartjs-plugin-annotation'
import useTranslate from "../../../hooks/useTranslate"
import { StroopGameStats } from "./StroopGameStats"
import { ArithmeticGameStats } from "./ArithmeticGameStats"
import { NBackGameStats } from "./NBackGameStats"
import { FruitReactionGameStats } from "./FruitReactionGameStats"

Chart.register(annotationPlugin)

function sameDay(d1: Date, d2: Date) {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
}

export function PatientGameStats() {
  // @ts-ignore
  const {id: patientId = ''} = useParams()
  const history = useHistory()
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [activeTab, setActiveTab] = useState<string>('stroop')
  const translateLocalazy = useTranslate()

  const setFilterToCurrentMonth = () => {
    const start = dayjs().startOf('month')
    const end = dayjs().endOf('month')
    setDateFrom(start.toDate())
    setDateTo(end.toDate())
  }
  const setFilterToPreviousMonth = () => {
    const start = dayjs().subtract(1, 'month').startOf('month')
    const end = dayjs().subtract(1, 'month').endOf('month')
    setDateFrom(start.toDate())
    setDateTo(end.toDate())
  }

  const {
    data: results,
    isLoading: isLoadingResults,
  } = useSWR(
    ['api.game.getGameResultsForPatient', patientId],
    () => apiV2.game.getGameResultsForPatient(patientId),
    {revalidateOnFocus: false}
  )

  const addIdsToPlayedGames = function <T extends { playedAt: string }>(games?: T[]) {
    return (games || [])
      .sort((a, b) => dayjs(b.playedAt).diff(a.playedAt, 'seconds'))
      .map((e, i) => ({...e, id: i + 1}))
  }

  const filterGamesByDate = function <T extends { playedAt: string }>(games: T[]) {
    return games.filter(e => {
      if (dateFrom && dayjs(e.playedAt).isBefore(dayjs(dateFrom).startOf('day'))) {
        return false
      }

      if (dateTo && dayjs(e.playedAt).isAfter(dayjs(dateTo).endOf('day'))) {
        return false
      }

      return true
    })
  }

  const fullData = useMemo(() => ({
    stroop: addIdsToPlayedGames(results?.stroop?.playedGames),
    arithmetic: addIdsToPlayedGames(results?.arithmetic?.playedGames),
    nBack: addIdsToPlayedGames(results?.nBack?.playedGames),
    fruitReaction: addIdsToPlayedGames(results?.fruitReaction?.playedGames),
  }), [results])

  const filteredData = useMemo(() => ({
    stroop: filterGamesByDate(fullData.stroop),
    arithmetic: filterGamesByDate(fullData.arithmetic),
    nBack: filterGamesByDate(fullData.nBack),
    fruitReaction: filterGamesByDate(fullData.fruitReaction),
  }), [results, dateFrom, dateTo])

  const handleCancel = useCallback(() => {
    history.push(`${RouteNames.PatientCard}/${patientId}`)
  }, [])

  if (isLoadingResults) {
    return <div className="loader-holder">
      <div className="loader"></div>
    </div>
  }

  return <div className="patient-game-stats">
    <div className="header flex justify-between">
      <div className="back-button flex align-center">
        <BackIcon onClick={handleCancel}/>
        <p onClick={handleCancel}>{translate('back')}</p>
      </div>
      <div className="flex gap">
        <div className="flex flex-column gap">
          <div className="flex justify-end align-center gap">
            <span>{translateLocalazy('patient.game-stats.filter-from')}</span>
            <DatePicker oneTap size="sm"
                        value={dateFrom}
                        onChange={setDateFrom}
                        renderCell={date => {
                          const gamesInThisDay = fullData[activeTab].filter(e => sameDay(new Date(e.playedAt), date)).length
                          const day = date.getDate()

                          if (gamesInThisDay > 0) {
                            return <span className="day-with-games">{day}</span>
                          }

                          return day
                        }}/>
          </div>
          <div className="flex justify-end align-center gap">
            <span>{translateLocalazy('patient.game-stats.filter-to')}</span>
            <DatePicker oneTap size="sm"
                        value={dateTo}
                        onChange={setDateTo}
                        renderCell={date => {
                          const gamesInThisDay = fullData[activeTab].filter(e => sameDay(new Date(e.playedAt), date)).length
                          const day = date.getDate()

                          if (gamesInThisDay > 0) {
                            return <span className="day-with-games">{day}</span>
                          }

                          return day
                        }}/>
          </div>
        </div>

        <div className="flex flex-column gap">
          <button className="g_btn g_btn--primary g_btn--small" type="button" onClick={setFilterToCurrentMonth}>
            {translateLocalazy('patient.game-stats.filter-this-month')}
          </button>
          <button className="g_btn g_btn--primary g_btn--small" type="button"
                  onClick={setFilterToPreviousMonth}>
            {translateLocalazy('patient.game-stats.filter-prev-month')}
          </button>
        </div>
      </div>
    </div>

    <Tabs defaultActiveKey="stroop" onSelect={(v) => setActiveTab(v as string)}>
      <Tabs.Tab eventKey="stroop" title={translateLocalazy('game.stroop.title')}>
        <StroopGameStats fullData={fullData.stroop} filteredData={filteredData.stroop}/>
      </Tabs.Tab>
      <Tabs.Tab eventKey="arithmetic" title={translateLocalazy('game.arithmetic.title')}>
        <ArithmeticGameStats fullData={fullData.arithmetic} filteredData={filteredData.arithmetic}/>
      </Tabs.Tab>
      <Tabs.Tab eventKey="nBack" title={translateLocalazy('game.n-back.title')}>
        <NBackGameStats fullData={fullData.nBack} filteredData={filteredData.nBack}/>
      </Tabs.Tab>
      <Tabs.Tab eventKey="fruitReaction" title={translateLocalazy('game.fruit-reaction.title')}>
        <FruitReactionGameStats fullData={fullData.fruitReaction} filteredData={filteredData.fruitReaction}/>
      </Tabs.Tab>
    </Tabs>
  </div>
}
